import { isDefined } from "@clipboard-health/util-ts";
import { useTheme } from "@mui/material";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { CircleProgress, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { startOfSecond } from "date-fns";

import { ShiftCircleProgressContent } from "./Content";
import { getShiftProgress } from "./utils/getShiftProgress";

interface ShiftCircleProgressProps {
  startDate: Date;
  endDate: Date;
  clockInDate?: Date;
  adjustedClockInDate?: Date;
  includeDatePill?: boolean;
  clockOutModalState: UseModalState;
  /** When provided, freezes the timer at this time instead of using the live clock. */
  frozenTime?: Date;
}

export function ShiftCircleProgress(props: ShiftCircleProgressProps) {
  const {
    startDate,
    endDate,
    clockInDate,
    adjustedClockInDate,
    includeDatePill = false,
    clockOutModalState,
    frozenTime,
  } = props;

  const currentDate = useCurrentDate(1000);
  const theme = useTheme();

  if (!isDefined(clockInDate)) {
    return null;
  }

  const now = isDefined(frozenTime) ? startOfSecond(frozenTime) : startOfSecond(currentDate);

  const {
    progress,
    secondaryProgress,
    timeWorked,
    timeLeft,
    isOvertime,
    overtimeText,
    totalTimeWorked,
    pendingProgress,
  } = getShiftProgress({
    clockInDate,
    adjustedClockInDate,
    shiftStartDate: startDate,
    shiftEndDate: endDate,
    now,
  });

  const hasExceededOvertimeThreshold = isOvertime && progress >= 1;

  return (
    <CircleProgress
      value={progress}
      color={
        hasExceededOvertimeThreshold
          ? theme.palette.intent?.warning.border
          : theme.palette.text.primary
      }
      secondaryValue={isOvertime ? secondaryProgress : undefined}
      secondaryColor={theme.palette.text.primary}
      pendingValue={isOvertime ? undefined : pendingProgress}
      pendingSecondaryValue={isOvertime ? pendingProgress : undefined}
      pendingColor={theme.palette.intent?.warning.border}
    >
      <ShiftCircleProgressContent
        hasExceededOvertimeThreshold={hasExceededOvertimeThreshold}
        includeDatePill={includeDatePill}
        shiftStartDate={startDate}
        isOvertime={isOvertime}
        progress={progress}
        overtimeText={overtimeText}
        totalTimeWorked={totalTimeWorked}
        timeWorked={timeWorked}
        timeLeft={timeLeft}
        clockOutModalState={clockOutModalState}
      />
    </CircleProgress>
  );
}
